import { useState } from "react";

import { MISSION_INFO } from "@/lib/constants";
import { slug } from "@/lib/format";
import { Modal } from "@/components/modal";
import { Button, Chip, Toggle } from "@/components/ui";
import { AgeStepper } from "@/components/wizard/step-preferences";

export const filterCount = (f) => Object.values(f).filter((v) => (Array.isArray(v) ? v.length : v !== undefined && v !== "" && v !== false)).length;

export function filtersToQuery(f) {
  const q = new URLSearchParams();
  Object.entries(f).forEach(([k, v]) => {
    if (Array.isArray(v)) { if (v.length) q.set(k, v.join(",")); }
    else if (v !== undefined && v !== "" && v !== false) q.set(k, String(v));
  });
  const s = q.toString();
  return s ? `?${s}` : "";
}

// One horizontal chip row; "Any" clears the value.
function ChipRow({ options, value, onChange, testId, labels }) {
  const multi = Array.isArray(value);
  const isOn = (o) => (multi ? value.includes(o) : value === o);
  return (
    <div className="no-scrollbar flex gap-2 overflow-x-auto pb-1">
      <Chip testId={`${testId}-any`} label="Any" selected={multi ? !value.length : !value} onClick={() => onChange(multi ? [] : undefined)} />
      {options.map((o) => (
        <Chip key={o} testId={`${testId}-${slug(o)}`} label={labels?.[o] ?? o} selected={isOn(o)} onClick={() => {
          if (!multi) return onChange(value === o ? undefined : o);
          onChange(value.includes(o) ? value.filter((x) => x !== o) : [...value, o]);
        }} />
      ))}
    </div>
  );
}

const L = ({ children }) => <p className="mb-2 mt-5 text-sm font-bold">{children}</p>;

export function FiltersDialog({ open, onClose, value, onApply, options }) {
  const [f, setF] = useState(value);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const missionLabels = Object.fromEntries(options.missions.map((m) => [m, MISSION_INFO[m].label]));
  return (
    <Modal open={open} onClose={onClose} title="Filter matches" testId="filters-sheet" wide>
      <p className="text-xs text-muted-foreground">Filters narrow your eligible matches. They never show members outside your preferences.</p>
      <L>Mission</L>
      <ChipRow testId="filter-mission" options={options.missions} labels={missionLabels} value={f.mission} onChange={set("mission")} />
      <L>Age range</L>
      <div className="flex gap-3">
        <AgeStepper testId="filter-min-age" label="Minimum" value={f.min_age ?? 18} onChange={set("min_age")} />
        <AgeStepper testId="filter-max-age" label="Maximum" value={f.max_age ?? 60} onChange={set("max_age")} />
      </div>
      <L>Gender</L>
      <ChipRow testId="filter-gender" options={options.genders} value={f.gender} onChange={set("gender")} />
      <L>Country (nationality)</L>
      <ChipRow testId="filter-country" options={options.countries} value={f.country} onChange={set("country")} />
      <L>Country of residence</L>
      <ChipRow testId="filter-residence" options={options.countries} value={f.residence} onChange={set("residence")} />
      <label htmlFor="filter-occupation" className="mb-2 mt-5 block text-sm font-bold">Occupation</label>
      <input id="filter-occupation" data-testid="filter-occupation-input" value={f.occupation ?? ""} onChange={(e) => set("occupation")(e.target.value)} placeholder="e.g. Engineer"
        className="h-12 w-full rounded-xl border border-border px-4 text-[15px] focus:border-primary focus:outline-none" />
      <L>Shared interests</L>
      <ChipRow testId="filter-interests" options={options.interests} value={f.interests ?? []} onChange={set("interests")} />
      <div className="mt-5 flex items-center justify-between rounded-xl bg-[#F4EBE6] p-4">
        <span className="text-sm font-bold">Verified members only</span>
        <Toggle testId="filter-verified-switch" label="Verified members only" checked={!!f.verified_only} onChange={set("verified_only")} />
      </div>
      <div className="mt-6 flex gap-3">
        <Button testId="filters-clear-button" title="Clear filters" variant="outline" className="flex-1" onClick={() => { setF({}); onApply({}); onClose(); }} />
        <Button testId="filters-apply-button" title="Apply" className="flex-1" onClick={() => { onApply(f); onClose(); }} />
      </div>
    </Modal>
  );
}
